import React, { useRef, useState, Suspense, useEffect } from "react";
import { useFormik } from "formik";
import { validateProfileData } from "../../schema";
import {
  updateImageS3,
  updateProfileAPI,
  changePasswordAPI,
} from "../../API/AdminProfileAPI/AdminProfile";
import ButtonLoader from "../../components/button/ButtonLoader";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { AdminProfileAPI } from "../../API/auth/SigninAction";
import DummyImage from "../../components/image/DummyImage";
import { FiSettings, FiUser, FiCamera, FiSave, FiEdit3, FiLock } from "react-icons/fi";
import { changePasswordSchema } from "../../schema";
import InputField from "../../components/Form/InputField";

// Lazy load components
const Wrapper = React.lazy(() => import("../../components/wrapper/Wrapper"));

const Setting = () => {
  const myRef = useRef();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const { AdminProfile } = useSelector((state) => state?.adminProfile);
  const dispatch = useDispatch();

  const initialValues = {
    firstName: "",
    lastName: "",
    profileImage: "",
  };

  const { values, setValues, handleChange, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema: validateProfileData,
      onSubmit: async (values) => {
        setUpdatingProfile(true);
        await updateProfileAPI({
          first_name: values.firstName,
          last_name: values.lastName,
          profileImage: values.profileImage,
        })
          .then((res) => {
            dispatch({
              type: "ADMIN_PROFILE",
              payload: res,
            });
            setUpdatingProfile(false);
            setHasChanged(false);
            toast.success("Profile updated successfully");
          })
          .catch(() => {
            setUpdatingProfile(false);
            toast.error("Error updating profile");
          });
      },
    });

  // Password change form
  const passwordInitialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const {
    values: passwordValues,
    handleChange: handlePasswordChange,
    handleSubmit: handlePasswordSubmit,
    touched: passwordTouched,
    errors: passwordErrors,
    resetForm: resetPasswordForm,
  } = useFormik({
    initialValues: passwordInitialValues,
    validationSchema: changePasswordSchema,
    onSubmit: async (values) => {
      setChangingPassword(true);
      try {
        await changePasswordAPI({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        });
        toast.success("Password changed successfully");
        resetPasswordForm();
        setPasswordChanged(false);
      } catch (error) {
        if (error.response?.data?.error?.message) {
          toast.error(error.response.data.error.message);
        } else {
          toast.error("Error changing password");
        }
      } finally {
        setChangingPassword(false);
      }
    },
  });

  const handlePasswordFieldChange = (e) => {
    handlePasswordChange(e);
    setPasswordChanged(true);
  };

  useEffect(() => {
    if (!AdminProfile) {
      setLoader(true);
      dispatch(AdminProfileAPI()).then(() => {
        setLoader(false);
      });
    } else {
      setValues({
        firstName: AdminProfile?.first_name || "",
        lastName: AdminProfile?.last_name || "",
        profileImage: AdminProfile?.profileImage || "",
      });
    }
  }, [dispatch, AdminProfile, setValues]);

  const handleUploadProfileImage = () => {
    myRef.current.click();
  };

  const handleProfilePicture = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsImageLoading(true);
      try {
        const ImageS3Url = await updateImageS3(file);
        setValues((prev) => ({
          ...prev,
          profileImage: ImageS3Url?.photos[0],
        }));
        setHasChanged(true);
      } catch {
        toast.error("Failed to upload image");
      } finally {
        setIsImageLoading(false);
      }
    }
  };

  const checkForChanges = (e) => {
    handleChange(e);
    const { name, value } = e.target;
    if (values[name] !== value) {
      setHasChanged(true);
    }
  };

  if (loader) {
    return (
      <div className="py-10 w-full flex justify-center items-center">
        <ButtonLoader />
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="w-full pt-40 flex justify-center items-center">
          <ButtonLoader />
        </div>
      }
    >
      <Wrapper>
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FiSettings className="w-8 h-8 text-[--primary]" />
            Settings
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your profile information and preferences
          </p>
        </div>

        {/* Profile Settings Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FiUser className="w-5 h-5" />
              Profile Information
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Update your personal information and profile picture
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {/* Profile Picture Section */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative group">
                <div
                  onClick={handleUploadProfileImage}
                  className="relative cursor-pointer w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200 hover:border-[--primary] transition-colors"
                >
                  <div
                    className={`absolute inset-0 flex items-center justify-center ${
                      isImageLoading
                        ? "bg-gray-600 bg-opacity-50 animate-pulse"
                        : ""
                    }`}
                  >
                    {isImageLoading ? (
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : values.profileImage ? (
                      <img
                        src={
                          values.profileImage.startsWith("https://")
                            ? values.profileImage
                            : `https://tykoonbucket.s3.amazonaws.com/${values.profileImage}`
                        }
                        alt="profile-image"
                        className="w-full h-full object-cover"
                        loading="eager"
                      />
                    ) : (
                      <DummyImage customClass="w-full h-full" />
                    )}
                  </div>
                  
                  {/* Camera Icon Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <FiCamera className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  {/* Edit Button */}
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-[--primary] rounded-full flex items-center justify-center shadow-lg hover:bg-[--primary]/90 transition-colors">
                    <FiEdit3 className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mt-4 text-center">
                Click on the image to upload a new profile picture
              </p>
              
              <input
                ref={myRef}
                type="file"
                hidden
                onChange={handleProfilePicture}
                accept="image/*"
              />
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  name="firstName"
                  id="firstName"
                  type="text"
                  placeholder="Enter your first name"
                  value={values.firstName}
                  onChange={checkForChanges}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors"
                />
                {touched.firstName && errors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  name="lastName"
                  id="lastName"
                  type="text"
                  placeholder="Enter your last name"
                  value={values.lastName}
                  onChange={checkForChanges}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors"
                />
                {touched.lastName && errors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!hasChanged || isImageLoading || updatingProfile}
                className="bg-[--primary] text-white px-6 py-3 rounded-lg hover:bg-[--primary]/90 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {updatingProfile ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Update Profile
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Change Password Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mt-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FiLock className="w-5 h-5" />
              Change Password
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Update your password to keep your account secure
            </p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="p-6">
            <div className="space-y-4">
              <InputField
                label={"Current Password"}
                id={"currentPassword"}
                name={"currentPassword"}
                type={"password"}
                placeholder={"Enter your current password"}
                value={passwordValues.currentPassword}
                onChange={handlePasswordFieldChange}
                onBlur={() => {}}
                error={passwordErrors.currentPassword}
                touch={passwordTouched.currentPassword}
              />

              <InputField
                label={"New Password"}
                id={"newPassword"}
                name={"newPassword"}
                type={"password"}
                placeholder={"Enter your new password"}
                value={passwordValues.newPassword}
                onChange={handlePasswordFieldChange}
                onBlur={() => {}}
                error={passwordErrors.newPassword}
                touch={passwordTouched.newPassword}
              />

              <InputField
                label={"Confirm New Password"}
                id={"confirmPassword"}
                name={"confirmPassword"}
                type={"password"}
                placeholder={"Confirm your new password"}
                value={passwordValues.confirmPassword}
                onChange={handlePasswordFieldChange}
                onBlur={() => {}}
                error={passwordErrors.confirmPassword}
                touch={passwordTouched.confirmPassword}
              />
            </div>

            {/* Change Password Button */}
            <div className="flex justify-end mt-6">
              <button
                type="submit"
                disabled={!passwordChanged || changingPassword}
                className="bg-[--primary] text-white px-6 py-3 rounded-lg hover:bg-[--primary]/90 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {changingPassword ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Changing...
                  </>
                ) : (
                  <>
                    <FiLock className="w-4 h-4" />
                    Change Password
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </Wrapper>
    </Suspense>
  );
};

export default Setting;
